import moment from 'moment';
import { deleteRemovedTimetableItems, formatTimelineItems, SaveTimetableArgs } from '@modules/timetable';
import { apolloClient } from '@lib/apollo';
import {
  INSERT_TIMETABLE_ITEM,
  insert_timetable_itemMutation,
  insert_timetable_itemMutationVariables,
  UPDATE_TIMETABLE_ITEM,
  update_timetable_itemMutation,
  update_timetable_itemMutationVariables,
} from '@app-types/graphql-types';
import { logError } from '@lib/logger';

export const saveTimetable = async ({
  timelineItems,
  week,
  batchId,
  batchSubjectConfigurations,
  setIsTimetableSaving,
  addToast,
  classIds,
  publish,
  subjectsData,
  setInitialTimetableSlotCounts,
  updateBatchClassBatchSubjectConfigurations,
  updateBatchClassBatchSubjectExtraSlots,
  removedTimelineItems,
  userId,
}: SaveTimetableArgs): Promise<void> => {
  if (batchId && week && batchSubjectConfigurations) {
    setIsTimetableSaving(true);

    try {
      const startOfWeek = moment().year(week.year()).week(week.week()).startOf('isoWeek');
      const endOfWeek = moment().year(week.year()).week(week.week()).endOf('isoWeek');

      const [newTimelineItems, existingTimelineItems] = formatTimelineItems({
        timelineItems,
        startOfWeek,
        endOfWeek,
        classIds,
        publish,
        userId,
      });

      // Add new timeline items
      if (newTimelineItems.length > 0) {
        await apolloClient.mutate<insert_timetable_itemMutation, insert_timetable_itemMutationVariables>({
          mutation: INSERT_TIMETABLE_ITEM,
          variables: {
            objects: newTimelineItems,
          },
        });
      }

      // Update existing timeline items
      if (existingTimelineItems.length > 0) {
        await Promise.all(
          existingTimelineItems.map(async (existingTimelineItem) => {
            await apolloClient.mutate<update_timetable_itemMutation, update_timetable_itemMutationVariables>({
              mutation: UPDATE_TIMETABLE_ITEM,
              variables: existingTimelineItem,
            });
          }),
        );
      }

      // Remove removed items
      if (removedTimelineItems.length > 0) {
        await deleteRemovedTimetableItems({
          startOfWeek,
          endOfWeek,
          batchId,
          removedTimelineItems,
        });
      }

      // Update timetable item count
      await updateBatchClassBatchSubjectConfigurations();
      await updateBatchClassBatchSubjectExtraSlots();

      setInitialTimetableSlotCounts({
        subjectData: subjectsData,
      });

      addToast('Successfully saved the timetable.', {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (error) {
      addToast('Error. Something went wrong. Please try again.', {
        appearance: 'error',
        autoDismiss: true,
      });

      logError(error);
    }

    setIsTimetableSaving(false);
  }
};
